import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { usePartner } from "@/context/PartnerContext";
import { useApp } from "@/context/AppContext";
import { computeMonthly } from "@/lib/lease";
import HrefLangAlternates from "@/components/HrefLangAlternates";
import SubsidyHint from "@/components/SubsidyHint";
import {
  ArrowUpRight, ArrowRight, Sparkles, Clock, Workflow, HeartHandshake,
  ShieldCheck, Plus, Minus, MessageSquare, X, ExternalLink,
  Monitor, Lock, Car, Camera, Wifi, Lightbulb, Activity, KeyRound, Cpu, Boxes, Droplet, Wrench,
} from "lucide-react";

const ICONS = {
  Sparkles, Clock, Workflow, HeartHandshake, ShieldCheck,
  Monitor, Lock, Car, Camera, Wifi, Lightbulb, Activity, KeyRound, Cpu, Boxes, Droplet, Wrench,
};
const Icon = ({ name, className = "w-5 h-5" }) => {
  const C = ICONS[name] || Sparkles;
  return <C className={className} />;
};

// Read partner.landing_copy[key] with a fallback. Language-aware: tries
// landing_copy[key_with_lang_suffix] first, then the canonical (nl) key,
// then the literal fallback.
const t = (p, key, fallback, lang) => {
  if (lang && lang !== "nl" && key.endsWith("_nl")) {
    const altKey = key.replace(/_nl$/, `_${lang}`);
    if (p?.landing_copy?.[altKey]) return p.landing_copy[altKey];
  }
  return p?.landing_copy?.[key] || fallback;
};

// Read p[baseKey + "_" + lang] with NL fallback, then literal fallback.
// Use for top-level partner fields like `landing_headline_nl`.
const tp = (p, baseKey, lang, fallback) =>
  p?.[`${baseKey}_${lang}`] || p?.[`${baseKey}_nl`] || fallback;

// Read item.field_lang with NL fallback. Used for array items (steps, tech_categories).
const ti = (item, field, lang, fallback = "") =>
  item?.[`${field}_${lang}`] || item?.[`${field}_nl`] || item?.[field] || fallback;

/**
 * Editorial-luxury Partner Marketing Landing, Aman × BEX hospitality blend.
 * Rendered at /p/<slug>/ when partner.landing_enabled === true.
 * Fully data-driven via /api/partners/<slug>/landing.
 */
export default function PartnerLandingPage() {
  const { partner, prefix } = usePartner();
  const { formatPrice, leaseSettings, lang } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!partner?.slug) return;
    api.get(`/partners/${partner.slug}/landing`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [partner?.slug]);

  if (!partner || !data) {
    return <div className="container-x py-20 text-center" data-testid="partner-landing-loading">Laden…</div>;
  }

  const p = data.partner;
  const headlineMeta = tp(p, "landing_headline", lang, p.hero_title_nl || p.name);
  const sublineMeta = tp(p, "landing_subline", lang, p.hero_subtitle_nl || p.tagline_nl || "");

  return (
    <div className="partner-landing-luxe" data-testid="partner-landing-page">
      <Helmet>
        <title>{`${headlineMeta}, ${p.name}`}</title>
        <meta name="description" content={sublineMeta} />
        <meta property="og:title" content={headlineMeta} />
        <meta property="og:description" content={sublineMeta} />
        <meta property="og:type" content="website" />
        {p.hero_image_url && <meta property="og:image" content={p.hero_image_url} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": p.name,
          "url": typeof window !== "undefined" ? `${window.location.origin}${prefix}` : "",
          "logo": p.logo_url || undefined,
          "description": sublineMeta || p.tagline_nl,
        })}</script>
      </Helmet>
      <HrefLangAlternates path={prefix || `/p/${p.slug}`} />
      <Hero p={p} prefix={prefix} nav={nav} lang={lang} />
      <Steps p={p} lang={lang} />
      <TechCategories p={p} prefix={prefix} lang={lang} />
      <LifestyleGallery p={p} lang={lang} />
      <CuratedPackages bundles={data.bundles} prefix={prefix} p={p}
        formatPrice={formatPrice} leaseSettings={leaseSettings} lang={lang} />
      {/* Subsidy hint banner, encourages buyers by showing local grant savings */}
      <section className="container-x py-6">
        <SubsidyHint amount={data.bundles?.[0]?.total_one_time_eur || 5000} variant="banner" />
      </section>
      <SoftwareTechSplit p={p} prefix={prefix} lang={lang} />
      <ServicePromise p={p} lang={lang} />
      <Testimonial p={p} lang={lang} />
      <ServiceCalculator p={p} lang={lang} />
      {p.landing_show_ecosystem !== false && <Ecosystem p={p} lang={lang} />}
      <Faq p={p} lang={lang} />
      <ContactBlock p={p} prefix={prefix} nav={nav} lang={lang} />
    </div>
  );
}

/* ─────────────────────────── Hero ─────────────────────────── */
function Hero({ p, prefix, nav, lang }) {
  const TRAITS = {
    nl: { seamless: "Naadloos op", live: "Live binnen 2–3 weken", invitation: "Een uitnodiging tot groei", contact_subj: "Kennismaking " },
    en: { seamless: "Seamless on", live: "Live within 2–3 weeks", invitation: "An invitation to grow", contact_subj: "Introduction meeting " },
    de: { seamless: "Nahtlos auf", live: "Live in 2–3 Wochen", invitation: "Eine Einladung zum Wachstum", contact_subj: "Kennenlerngespräch " },
    fr: { seamless: "Intégration native", live: "Opérationnel en 2–3 semaines", invitation: "Une invitation à la croissance", contact_subj: "Rencontre " },
    es: { seamless: "Integración nativa con", live: "En vivo en 2–3 semanas", invitation: "Una invitación a crecer", contact_subj: "Reunión introductoria " },
    it: { seamless: "Integrazione nativa con", live: "Operativo in 2–3 settimane", invitation: "Un invito alla crescita", contact_subj: "Incontro introduttivo " },
    da: { seamless: "Sømløst på", live: "Live inden for 2–3 uger", invitation: "En invitation til vækst", contact_subj: "Introduktionsmøde " },
  };
  const tr = TRAITS[lang] || TRAITS.nl;
  const tagline = tp(p, "tagline", lang, tr.invitation);
  const headline = tp(p, "landing_headline", lang, p.hero_title_nl);
  const subline = tp(p, "landing_subline", lang, p.hero_subtitle_nl);
  const ctaPrimary = tp(p, "landing_cta_primary", lang, "Stel jouw bookingszuil samen");
  const ctaSecondary = tp(p, "landing_cta_secondary", lang, "Plan een kennismaking");
  return (
    <section className="relative overflow-hidden luxe-grain" data-testid="landing-hero">
      <div className="container-x pt-24 pb-20 md:pt-32 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 luxe-fade">
          <div className="luxe-eyebrow">{tagline}</div>
          <h1 className="luxe-display text-[44px] sm:text-[56px] lg:text-[80px] mt-8">
            {headline}
          </h1>
          <p className="text-base md:text-lg mt-8 max-w-xl leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>
            {subline}
          </p>
          <div className="flex flex-wrap gap-3 mt-12">
            <button onClick={() => nav(`${prefix}/configurator`)} className="luxe-btn-primary inline-flex items-center gap-3" data-testid="landing-cta-primary">
              {ctaPrimary}
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <a href={`mailto:${p.contact_email || ""}?subject=${encodeURIComponent(tr.contact_subj + (p.name || ""))}`} className="luxe-btn-ghost inline-flex items-center gap-3" data-testid="landing-cta-secondary">
              {ctaSecondary}
            </a>
          </div>
          <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--luxe-muted)" }}>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> GDPR &amp; ISO 27001</span>
            <span className="inline-flex items-center gap-2">{tr.seamless} {p.name}</span>
            <span className="inline-flex items-center gap-2">{tr.live}</span>
          </div>
        </div>
        <div className="lg:col-span-5 luxe-fade" style={{ animationDelay: "0.15s" }}>
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden" style={{ borderRadius: 2 }}>
              {p.hero_image_url ? (
                <img src={p.hero_image_url} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full" style={{ background: "var(--luxe-cream-2)" }} />
              )}
            </div>
            {/* Editorial overlay caption */}
            <div className="absolute -bottom-10 left-6 right-10 md:left-10 md:right-14 p-7 luxe-card" style={{ background: "var(--luxe-cream)" }}>
              <div className="luxe-eyebrow text-[10px]">{t(p, "hero_in_practice_label_nl", "Naadloos in de praktijk", lang)}</div>
              <div className="luxe-display text-2xl mt-3" style={{ lineHeight: 1.15 }}>
                {t(p, "hero_in_practice_body_nl", "Gasten verwelkomen zichzelf. Jouw team heeft tijd voor het verhaal.", lang)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────── Lifestyle Gallery ──────────────────── */
function LifestyleGallery({ p, lang }) {
  const items = p.landing_gallery || [];
  if (!items.length) return null;
  const label = tp(p, "landing_gallery_label", lang, "Een eersteklas gastbeleving");
  const title = tp(p, "landing_gallery_title", lang, "Jouw park verdient technologie die net zo gastvrij is als jouw plek.");
  const body = tp(p, "landing_gallery_body", lang, "");

  return (
    <section className="luxe-section" data-testid="landing-gallery">
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12 md:mb-16">
          <div className="md:col-span-7">
            <div className="luxe-eyebrow">{label}</div>
            <h2 className="luxe-display text-4xl md:text-5xl mt-6 leading-tight">{title}</h2>
          </div>
          {body && (
            <div className="md:col-span-5">
              <hr className="luxe-rule mb-6" />
              <p className="text-base leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{body}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {items.map((g, idx) => {
            const layout = idx === 0
              ? "col-span-12 md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-[3/4]"
              : "col-span-6 md:col-span-5 aspect-square md:aspect-[16/10]";
            const caption = ti(g, "caption", lang, "");
            const alt = ti(g, "alt", lang, "");
            return (
              <figure
                key={idx}
                className={`relative overflow-hidden luxe-gallery-tile ${layout}`}
                style={{ borderRadius: 2 }}
              >
                <img
                  src={g.url}
                  alt={alt}
                  loading="lazy"
                  className="w-full h-full object-cover luxe-gallery-img"
                />
                {caption && (
                  <figcaption className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <div className="luxe-gallery-caption">
                      <div className="luxe-eyebrow text-[10px] mb-1" style={{ color: "var(--luxe-emerald)" }}>
                        0{idx + 1}
                      </div>
                      <div className="text-sm md:text-base font-medium leading-snug" style={{ color: "var(--luxe-ink)" }}>
                        {caption}
                      </div>
                    </div>
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── Tech Categories grid ────────────────── */
function TechCategories({ p, prefix, lang }) {
  const items = p.landing_tech_categories || [];
  const nav = useNavigate();
  const [waitlistCat, setWaitlistCat] = useState(null);
  if (!items.length) return null;
  const label = tp(p, "landing_tech_categories_label", lang, "Tech Marketplace");
  const title = tp(p, "landing_tech_categories_title", lang, "Eén ecosysteem voor élk onderdeel van jouw park.");
  const body = tp(p, "landing_tech_categories_body", lang, "");
  const soonLabel = { nl: "Binnenkort", en: "Coming soon", de: "Bald verfügbar", fr: "Bientôt", es: "Próximamente", it: "Prossimamente", da: "Kommer snart" }[lang] || "Binnenkort";
  const ctaAvailable = { nl: "Configureer", en: "Configure", de: "Konfigurieren", fr: "Configurer", es: "Configurar", it: "Configura", da: "Konfigurer" }[lang] || "Configureer";
  const ctaSoon = { nl: "Hou me op de hoogte", en: "Notify me", de: "Benachrichtigen", fr: "Me prévenir", es: "Avísame", it: "Avvisami", da: "Underret mig" }[lang] || "Hou me op de hoogte";

  const handleClick = (c) => {
    if (c.available === false) {
      setWaitlistCat(c);
      return;
    }
    // Available → drive into the configurator (the main conversion flow).
    // First Tech-Marketplace category is always Kiosks so /configurator is the right destination.
    nav(`${prefix}/configurator`);
  };

  return (
    <section className="luxe-section" data-testid="landing-tech-categories">
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12 md:mb-16">
          <div className="md:col-span-6">
            <div className="luxe-eyebrow">{label}</div>
            <h2 className="luxe-display text-4xl md:text-5xl mt-6 leading-tight">{title}</h2>
          </div>
          {body && (
            <div className="md:col-span-6">
              <hr className="luxe-rule mb-6" />
              <p className="text-base leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{body}</p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((c, i) => {
            const isSoon = c.available === false;
            return (
              <button
                type="button"
                key={i}
                onClick={() => handleClick(c)}
                className={`luxe-tech-card luxe-tech-card-clickable relative p-6 md:p-7 text-left ${isSoon ? "luxe-tech-card-soon" : ""}`}
                data-testid={`tech-category-${i}-card`}
                aria-label={ti(c, "title", lang)}
              >
                <div className="luxe-tech-icon flex items-center justify-center mb-5">
                  <Icon name={c.icon} className="w-6 h-6" />
                </div>
                <div className="flex items-start gap-2 mb-2">
                  <div className="luxe-display text-xl md:text-2xl leading-tight flex-1">{ti(c, "title", lang)}</div>
                  {isSoon && (
                    <span className="luxe-tech-chip text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap" data-testid={`tech-category-${i}-soon`}>
                      {soonLabel}
                    </span>
                  )}
                </div>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--luxe-ink-soft)" }}>{ti(c, "body", lang)}</p>
                <div className="luxe-tech-cta text-sm font-semibold inline-flex items-center gap-2">
                  {isSoon ? ctaSoon : ctaAvailable}
                  <ArrowRight className="w-4 h-4 luxe-tech-cta-arrow" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {waitlistCat && (
        <WaitlistModal
          category={waitlistCat}
          partnerSlug={p.slug}
          lang={lang}
          onClose={() => setWaitlistCat(null)}
        />
      )}
    </section>
  );
}

/* ────────────── Waitlist modal, captures interest for upcoming Tech-Marketplace categories ────────────── */
function WaitlistModal({ category, partnerSlug, lang, onClose }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const T = {
    nl: { title: "Hou me op de hoogte", body: "Laat je e-mail achter, we sturen je een bericht zodra deze categorie live gaat.", email: "E-mailadres", submit: "Aanmelden", thanks: "Bedankt! We houden je op de hoogte.", error: "Er ging iets mis, probeer opnieuw.", close: "Sluiten" },
    en: { title: "Notify me", body: "Drop your email, we'll let you know when this category goes live.", email: "Email address", submit: "Notify me", thanks: "Thanks! We'll keep you posted.", error: "Something went wrong, please try again.", close: "Close" },
    de: { title: "Benachrichtigen", body: "Hinterlassen Sie Ihre E-Mail, wir benachrichtigen Sie, sobald diese Kategorie verfügbar ist.", email: "E-Mail-Adresse", submit: "Benachrichtigen", thanks: "Danke! Wir halten Sie auf dem Laufenden.", error: "Etwas ist schiefgelaufen, bitte erneut versuchen.", close: "Schließen" },
    fr: { title: "Me prévenir", body: "Laissez votre email, nous vous préviendrons dès que cette catégorie sera disponible.", email: "Adresse email", submit: "Me prévenir", thanks: "Merci ! Nous vous tiendrons au courant.", error: "Une erreur est survenue, veuillez réessayer.", close: "Fermer" },
  }[lang] || {};

  const tr = T;
  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setStatus("submitting");
    try {
      await api.post("/leads/interest", {
        email,
        topic: (category.icon || "").toLowerCase() + "-" + (category.title_nl || "").toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        partner_slug: partnerSlug,
        category_title: ti(category, "title", lang),
        lang,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tech-category-waitlist-modal"
    >
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
      <div className="relative max-w-md w-full luxe-waitlist-card p-7 md:p-8" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center hover:bg-black/5"
          aria-label={tr.close}
          data-testid="tech-category-waitlist-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="luxe-tech-icon flex items-center justify-center mb-5">
          <Icon name={category.icon} className="w-5 h-5" />
        </div>
        <div className="luxe-eyebrow text-[10px]">{ti(category, "title", lang)}</div>
        <h3 className="luxe-display text-2xl md:text-3xl mt-3 leading-tight">{tr.title}</h3>
        <p className="text-sm md:text-base mt-3 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{tr.body}</p>

        {status === "success" ? (
          <div className="mt-6 luxe-waitlist-success p-4 text-sm" data-testid="tech-category-waitlist-success">
            ✓ {tr.thanks}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={tr.email}
              className="luxe-waitlist-input w-full px-4 py-3 text-sm"
              data-testid="tech-category-waitlist-email"
              disabled={status === "submitting"}
            />
            {status === "error" && (
              <div className="text-xs text-red-600">{tr.error}</div>
            )}
            <button
              type="submit"
              disabled={status === "submitting"}
              className="luxe-btn-primary w-full justify-center text-sm"
              data-testid="tech-category-waitlist-submit"
            >
              {status === "submitting" ? "…" : tr.submit}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ────────────── Software ⊕ Technology split ──────────────── */
function SoftwareTechSplit({ p, prefix, lang }) {
  const data = p.landing_software_split;
  if (!data) return null;
  const label = tp(p, "landing_software_split_label", lang, "Software ⊕ Technology");
  const title = tp(p, "landing_software_split_title", lang, "Software + Technology = compleet park-ecosysteem.");
  const body = tp(p, "landing_software_split_body", lang, "");
  const SoftSide = data.software_side || {};
  const TechSide = data.tech_side || {};
  return (
    <section className="luxe-section" data-testid="landing-software-tech-split">
      <div className="container-x">
        <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <div className="luxe-eyebrow">{label}</div>
          <h2 className="luxe-display text-4xl md:text-5xl mt-6 leading-tight">{title}</h2>
          {body && <p className="text-base md:text-lg mt-6 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{body}</p>}
        </div>
        <div className="grid md:grid-cols-[1fr_auto_1fr] items-stretch gap-4 md:gap-6">
          <SplitColumn side={SoftSide} variant="software" lang={lang} />
          <div className="hidden md:flex items-center justify-center">
            <div className="luxe-split-plus" aria-hidden="true">⊕</div>
          </div>
          <SplitColumn side={TechSide} variant="tech" lang={lang} />
        </div>
      </div>
    </section>
  );
}

function SplitColumn({ side, variant, lang }) {
  const items = (side[`items_${lang}`] || side.items_nl || []);
  const label = side[`label_${lang}`] || side.label_nl;
  const subtitle = side[`subtitle_${lang}`] || side.subtitle_nl;
  const linkLabel = side[`link_label_${lang}`] || side.link_label_nl || "Verken";
  return (
    <div className={`luxe-split-col luxe-split-col-${variant} p-7 md:p-8 flex flex-col`}>
      <div className="flex items-start gap-3 mb-2">
        <div className="luxe-tech-icon flex items-center justify-center" style={{ flexShrink: 0 }}>
          <Icon name={side.icon || "Boxes"} className="w-5 h-5" />
        </div>
        <div>
          <div className="luxe-eyebrow text-[10px]">{subtitle}</div>
          <div className="luxe-display text-2xl md:text-3xl mt-1 leading-tight">{label}</div>
        </div>
      </div>
      <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm" style={{ color: "var(--luxe-ink-soft)" }}>
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="luxe-split-bullet" aria-hidden="true" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {side.link_url && (
        <a
          href={side.link_url}
          target={side.link_url.startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
          className="luxe-btn-ghost mt-7 inline-flex items-center gap-2 self-start text-sm"
          data-testid={`split-${variant}-link`}
        >
          {linkLabel} <ArrowUpRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}


/* ────────────────────────── Steps ────────────────────────── */
function Steps({ p, lang }) {
  const steps = p.landing_steps || [];
  if (!steps.length) return null;
  return (
    <section className="luxe-section" data-testid="landing-steps">
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-5">
            <div className="luxe-eyebrow">{t(p, "steps_label_nl", "Zo werkt het", lang)}</div>
            <h2 className="luxe-display text-4xl md:text-5xl mt-6 leading-tight">
              {t(p, "steps_title_nl", "Van pakket naar live in drie stappen.", lang)}
            </h2>
          </div>
          <div className="md:col-span-7">
            <hr className="luxe-rule" />
            <div className="divide-y" style={{ borderColor: "var(--luxe-rule)" }}>
              {steps.map((s) => (
                <div key={s.n} className="py-8 flex gap-6 md:gap-8 items-start">
                  <div className="luxe-step-badge flex-shrink-0">
                    <span className="luxe-step-badge-num">0{s.n}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="luxe-display text-2xl md:text-3xl">{ti(s, "title", lang)}</div>
                    <p className="text-sm md:text-base mt-3 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{ti(s, "body", lang)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Curated Packages ─────────────── */
function CuratedPackages({ bundles, prefix, p, formatPrice, leaseSettings }) {
  const nav = useNavigate();
  if (!bundles?.length) return null;
  return (
    <section className="luxe-section" style={{ background: "var(--luxe-cream-2)" }} data-testid="landing-packages">
      <div className="container-x">
        <div className="max-w-2xl">
          <div className="luxe-eyebrow">{t(p, "packages_label_nl", "Kant-en-klare pakketten")}</div>
          <h2 className="luxe-display text-4xl md:text-5xl mt-6 leading-tight">
            {t(p, "packages_title_nl", "Door ons samengesteld. Door jou aanpasbaar.")}
          </h2>
          <p className="text-base mt-6 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>
            {t(p, "packages_subtitle_nl", `Drie pakketten die ${p.name}-klanten het meest kiezen.`)}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {bundles.map((b, i) => (
            <PackageCard key={b.id} b={b} prefix={prefix} nav={nav} p={p}
              formatPrice={formatPrice} leaseSettings={leaseSettings} delay={i * 0.07} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PackageCard({ b, prefix, nav, p, formatPrice, leaseSettings, delay = 0 }) {
  const investment = (b.hardware || []).reduce((s, h) => s + (Number(h.purchase_price) || 0), 0);
  const monthly = useMemo(
    () => computeMonthly(investment, leaseSettings).monthly,
    [investment, leaseSettings]
  );
  return (
    <div className="luxe-card overflow-hidden flex flex-col luxe-fade" style={{ animationDelay: `${delay}s` }}>
      <div className="aspect-[5/4] flex items-center justify-center overflow-hidden relative" style={{ background: "var(--luxe-cream)" }}>
        {b.image_url && <img src={b.image_url} alt={b.name_i18n?.nl} className="w-full h-full object-contain p-8" />}
        {b.discount_pct > 0 && (
          <div className="absolute top-5 right-5 px-3 py-1 text-[10px] font-semibold tracking-[0.18em] uppercase" style={{ background: "var(--luxe-ink)", color: "var(--luxe-cream)", borderRadius: 999 }}>
            −{b.discount_pct}%
          </div>
        )}
      </div>
      <div className="p-8 flex flex-col flex-1">
        <div className="luxe-eyebrow text-[10px]">{b.name_i18n?.nl?.split(" ")[0]}</div>
        <div className="luxe-display text-3xl mt-3">{b.name_i18n?.nl}</div>
        <p className="text-sm mt-4 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{b.description_i18n?.nl}</p>
        <hr className="luxe-rule my-6" />
        <ul className="space-y-2 text-sm">
          {(b.hardware || []).map((h) => (
            <li key={h.id} className="flex items-start gap-3"><span style={{ color: "var(--luxe-emerald)" }}>—</span><b>{h.name}</b></li>
          ))}
          {(b.software || []).map((s) => (
            <li key={s.id} className="flex items-start gap-3" style={{ color: "var(--luxe-ink-soft)" }}><span style={{ color: "var(--luxe-emerald)" }}>—</span>{s.name}</li>
          ))}
        </ul>
        <div className="mt-auto pt-8">
          <div className="flex items-baseline justify-between mb-5">
            <div className="luxe-italic text-base" style={{ color: "var(--luxe-muted)" }}>vanaf</div>
            <div className="luxe-display text-3xl" style={{ color: "var(--luxe-emerald)" }}>
              {formatPrice ? formatPrice(monthly) : `€ ${monthly.toFixed(0)}`}
              <span className="text-xs ml-1" style={{ color: "var(--luxe-muted)" }}>/ mnd</span>
            </div>
          </div>
          <button
            onClick={() => nav(`${prefix}/bundles/${b.id}`)}
            className="luxe-btn-primary w-full inline-flex items-center justify-center gap-3"
            data-testid={`package-cta-${b.slug}`}
          >
            {t(p, "package_cta_label_nl", "Bekijk & pas aan")}
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Testimonial (editorial cinematic break) ─────────── */
function Testimonial({ p }) {
  const t_obj = p.landing_testimonial;
  if (!t_obj?.quote_nl) return null;
  return (
    <section className="luxe-section" style={{ background: "var(--luxe-cream-2)" }} data-testid="landing-testimonial">
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <div className="luxe-eyebrow">Klantverhaal</div>
            <blockquote className="luxe-display luxe-italic text-3xl md:text-5xl mt-8 leading-[1.15]" style={{ color: "var(--luxe-ink)" }}>
              <span style={{ color: "var(--luxe-emerald)", fontSize: "1.5em", lineHeight: 0, position: "relative", top: "0.3em", marginRight: "0.05em" }}>"</span>
              {t_obj.quote_nl}
              <span style={{ color: "var(--luxe-emerald)", fontSize: "1.5em", lineHeight: 0, position: "relative", top: "0.3em", marginLeft: "0.05em" }}>"</span>
            </blockquote>
            <div className="mt-10 flex items-center gap-5">
              <div className="luxe-display text-3xl" style={{ color: "var(--luxe-emerald)", opacity: 0.7 }}>—</div>
              <div>
                <div className="luxe-display text-xl">{t_obj.author_name}</div>
                <div className="text-sm mt-0.5" style={{ color: "var(--luxe-ink-soft)" }}>
                  {t_obj.author_role_nl}{t_obj.company_name ? `, ${t_obj.company_name}` : ""}
                </div>
              </div>
            </div>
          </div>
          {Array.isArray(t_obj.stats) && t_obj.stats.length > 0 && (
            <div className="md:col-span-5 mt-8 md:mt-0">
              <hr className="luxe-rule mb-2" />
              <div className="divide-y" style={{ borderColor: "var(--luxe-rule)" }}>
                {t_obj.stats.map((s, i) => (
                  <div key={i} className="py-6 flex items-baseline justify-between gap-6">
                    <div className="luxe-display text-5xl md:text-6xl" style={{ color: "var(--luxe-emerald)" }}>{s.value}</div>
                    <div className="luxe-italic text-base text-right max-w-[220px]" style={{ color: "var(--luxe-ink-soft)" }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <hr className="luxe-rule mt-2" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Service Promise (right tone, editorial) ─────────── */
function ServicePromise({ p }) {
  const sp = p.landing_service_promise;
  if (!sp) return null;
  return (
    <section className="luxe-section" data-testid="landing-service-promise">
      <div className="container-x grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="luxe-eyebrow">{t(p, "service_promise_label_nl", "De juiste invalshoek")}</div>
          <h2 className="luxe-display text-4xl md:text-6xl mt-6 leading-[1.05]">
            <span className="luxe-italic">{sp.headline_nl}</span>
          </h2>
          <p className="text-base mt-8 leading-relaxed max-w-md" style={{ color: "var(--luxe-ink-soft)" }}>{sp.body_nl}</p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-12">
          {(sp.points || []).map((pt, i) => (
            <div key={i}>
              <div className="luxe-display text-5xl" style={{ color: "var(--luxe-emerald)", opacity: 0.5 }}>0{i + 1}</div>
              <div className="luxe-display text-2xl mt-3">{pt.title}</div>
              <p className="text-sm mt-3 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{pt.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────── Service-uitbreidings calculator ───────────── */
function ServiceCalculator({ p }) {
  const cfg = p.landing_calculator;
  const [arrivals, setArrivals] = useState(cfg?.default_arrivals_per_week || 80);
  const [units, setUnits] = useState(cfg?.default_units || 1);
  if (!cfg) return null;

  const minutes = cfg.minutes_per_checkin || 8;
  const rate = (cfg.automation_rate_pct || 70) / 100;
  const extraOpenHoursPerDay = cfg.extra_open_hours_per_day || 14;

  const handlingsPerYear = arrivals * 52 * units;
  const minutesAutomated = handlingsPerYear * minutes * rate;
  const hoursReturned = Math.round(minutesAutomated / 60);
  const extraOpenHoursPerYear = Math.round(extraOpenHoursPerDay * 365 * units);

  return (
    <section className="luxe-section" style={{ background: "#0E2620", color: "#FFFFFF" }} data-testid="landing-calculator">
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="luxe-eyebrow" style={{ color: "#86EFAC" }}>{t(p, "calculator_label_nl", "Service-impact")}</div>
          <h2 className="luxe-display text-4xl md:text-5xl mt-6 leading-tight" style={{ color: "#FFFFFF" }}>{cfg.headline_nl}</h2>
          <p className="text-base md:text-lg mt-6 leading-relaxed max-w-2xl" style={{ color: "rgba(255,255,255,0.88)" }}>{cfg.body_nl}</p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="border p-8" style={{ borderColor: "rgba(255,255,255,0.22)", borderRadius: 12, background: "rgba(255,255,255,0.04)" }}>
              <div className="luxe-eyebrow" style={{ color: "#86EFAC" }}>Jouw situatie</div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold mt-8 mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>
                Aankomsten per week per locatie
              </label>
              <input type="range" min="10" max="500" step="5" value={arrivals}
                onChange={(e) => setArrivals(Number(e.target.value))}
                className="w-full"
                style={{ accentColor: "#09954F" }}
                data-testid="calc-arrivals"
              />
              <div className="luxe-display text-3xl mt-2" style={{ color: "#FFFFFF" }}>{arrivals}<span className="text-base ml-2 font-normal" style={{ color: "rgba(255,255,255,0.7)", fontFamily: "Lato, sans-serif" }}>/ week</span></div>

              <label className="block text-xs uppercase tracking-[0.18em] font-semibold mt-10 mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>
                Locaties / bookingszuilen
              </label>
              <div className="flex items-center gap-5">
                <button onClick={() => setUnits(Math.max(1, units - 1))} className="w-10 h-10 rounded-full border flex items-center justify-center hover:bg-white/10" style={{ borderColor: "rgba(255,255,255,0.32)", color: "#FFFFFF" }} data-testid="calc-units-minus"><Minus className="w-4 h-4" /></button>
                <div className="luxe-display text-3xl w-14 text-center" style={{ color: "#FFFFFF" }}>{units}</div>
                <button onClick={() => setUnits(units + 1)} className="w-10 h-10 rounded-full border flex items-center justify-center hover:bg-white/10" style={{ borderColor: "rgba(255,255,255,0.32)", color: "#FFFFFF" }} data-testid="calc-units-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <p className="text-xs mt-8 leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
                Aannames: {minutes} min per check-in · {Math.round(rate * 100)}% door zuil afgehandeld · {extraOpenHoursPerDay} u extra open per dag.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            <ResultPlate label="Service-uren teruggegeven" value={hoursReturned.toLocaleString("nl-NL")} unit="uur per jaar"
              sub="Tijd voor persoonlijk contact, tips en het werk waar je medewerkers voor in de recreatie zitten."
              dataTestId="calc-result-hours" />
            <ResultPlate label="Extra openingstijd" value={extraOpenHoursPerYear.toLocaleString("nl-NL")} unit="uur per jaar"
              sub="Late aankomsten, vroege vertrekken, faciliteit-toegang in het weekend, gewoon mogelijk."
              dataTestId="calc-result-open" />
          </div>
        </div>
      </div>
    </section>
  );
}
function ResultPlate({ label, value, unit, sub, dataTestId }) {
  return (
    <div className="border p-8 flex flex-col h-full" style={{ borderColor: "rgba(255,255,255,0.22)", borderRadius: 12, background: "rgba(255,255,255,0.04)" }} data-testid={dataTestId}>
      <div className="luxe-eyebrow" style={{ color: "#86EFAC" }}>{label}</div>
      <div className="luxe-display text-6xl md:text-7xl mt-6" style={{ color: "#FFFFFF" }}>{value}</div>
      <div className="text-base mt-2 font-medium" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Lato, sans-serif" }}>{unit}</div>
      <p className="text-sm mt-6 leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>{sub}</p>
    </div>
  );
}

/* ─────────── Ecosystem partner showcase ─────────── */
function Ecosystem({ p }) {
  const list = p.ecosystem_partners || [];
  const [active, setActive] = useState(null);
  if (!list.length) return null;
  return (
    <section className="luxe-section" data-testid="landing-ecosystem">
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <div className="luxe-eyebrow">{t(p, "ecosystem_label_nl", "BEX App Store ecosysteem")}</div>
            <h2 className="luxe-display text-4xl md:text-6xl mt-6 leading-[1.05]">
              {t(p, "ecosystem_title_nl", "De partners doen het digitale werk. De bookingszuil doet de rest.")}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>
              {t(p, "ecosystem_subtitle_nl", "Je partners in de BEX App Store zijn briljant in wat ze doen, vooral digitaal. De bookingszuil is wat er gebeurt buiten openingstijd, voor de gasten die liever géén app gebruiken, en voor het moment dat receptie 'dat regel je even zelf' wil zeggen, vriendelijk, en zonder onderbreking.")}
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((ep, i) => (
            <EcosystemCard key={ep.id} ep={ep} onOpen={() => setActive(ep)} delay={i * 0.06} />
          ))}
        </div>
      </div>
      {active && <EcosystemModal p={p} ep={active} onClose={() => setActive(null)} />}
    </section>
  );
}

function EcosystemCard({ ep, onOpen, delay }) {
  const [logoIdx, setLogoIdx] = useState(0);
  const logoSources = useMemo(() => {
    const list = [];
    if (ep.logo_url) list.push(ep.logo_url);
    if (ep.website_url) {
      try {
        const host = new URL(ep.website_url).hostname.replace(/^www\./, "");
        list.push(`https://logo.clearbit.com/${host}?size=128`);
        list.push(`https://www.google.com/s2/favicons?sz=128&domain=${host}`);
      } catch { /* ignore */ }
    }
    return list;
  }, [ep.logo_url, ep.website_url]);
  const currentLogo = logoSources[logoIdx];
  return (
    <button
      onClick={onOpen}
      className="luxe-card text-left group luxe-fade flex flex-col"
      style={{ animationDelay: `${delay}s` }}
      data-testid={`ecosystem-card-${ep.slug}`}
    >
      <div className="flex items-center justify-between p-7 pb-5" style={{ borderBottom: "1px solid var(--luxe-rule)" }}>
        <div className="h-10 flex items-center">
          {currentLogo ? (
            <img
              key={currentLogo}
              src={currentLogo}
              alt={ep.name}
              className="max-h-9 max-w-[140px] object-contain"
              onError={() => setLogoIdx((i) => i + 1)}
              onLoad={(e) => { if (e.currentTarget.naturalWidth === 0) setLogoIdx((i) => i + 1); }}
            />
          ) : (
            <div className="luxe-display text-2xl" style={{ color: ep.brand_color || "var(--luxe-emerald)" }}>{ep.name}</div>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5" style={{ background: "var(--luxe-emerald-soft)", color: "var(--luxe-emerald)", borderRadius: 999 }}>
          {ep.category}
        </span>
      </div>
      <div className="p-7 pt-6 flex-1 flex flex-col">
        {ep.combo_headline_nl ? (
          <div className="luxe-display text-xl md:text-[22px] leading-tight" style={{ color: "var(--luxe-ink)", fontWeight: 700 }}>
            {ep.combo_headline_nl}
          </div>
        ) : (
          <div className="luxe-display text-xl leading-snug" style={{ color: "var(--luxe-emerald)" }}>{ep.tagline_nl}</div>
        )}
        <p className="text-sm mt-4 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>{ep.short_pitch_nl}</p>
        <div className="mt-auto pt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold group-hover:gap-3 transition-all" style={{ color: "var(--luxe-emerald)" }}>
          Lees meer over {ep.name} <ArrowUpRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </button>
  );
}

function EcosystemModal({ p, ep, onClose }) {
  const [heroLogoIdx, setHeroLogoIdx] = useState(0);
  const heroLogoSources = useMemo(() => {
    const list = [];
    if (ep.logo_url) list.push(ep.logo_url);
    if (ep.website_url) {
      try {
        const host = new URL(ep.website_url).hostname.replace(/^www\./, "");
        list.push(`https://logo.clearbit.com/${host}?size=256`);
        list.push(`https://www.google.com/s2/favicons?sz=256&domain=${host}`);
      } catch { /* ignore */ }
    }
    return list;
  }, [ep.logo_url, ep.website_url]);
  const currentHeroLogo = heroLogoSources[heroLogoIdx];

  // Soft tint of partner brand color for hero background (~6% alpha)
  const brand = ep.brand_color || "#0F5C3B";
  const heroBg = `linear-gradient(160deg, ${brand}10 0%, var(--luxe-cream) 60%)`;
  const heroAccent = `${brand}26`;

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const endorsement = ep.bex_endorsement;
  const authorInitials = (endorsement?.author_name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 md:p-10"
      style={{ background: "rgba(15,26,20,0.55)", backdropFilter: "blur(8px)" }}
      data-testid="ecosystem-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl my-auto luxe-card relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--luxe-cream)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5 z-10"
          style={{ background: "rgba(255,255,255,0.7)", backdropFilter: "blur(6px)" }}
          data-testid="ecosystem-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* ───── HERO ───── */}
        <div
          className="px-10 md:px-16 pt-14 pb-12 text-center relative"
          style={{ background: heroBg, borderBottom: "1px solid var(--luxe-rule)" }}
          data-testid={`ecosystem-modal-hero-${ep.slug}`}
        >
          <div
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1.5 mb-6"
            style={{ background: heroAccent, color: brand, borderRadius: 999 }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: brand }} />
            Booking Experts App Store Partner
          </div>

          <div className="h-24 md:h-28 flex items-center justify-center mb-6">
            {currentHeroLogo ? (
              <img
                key={currentHeroLogo}
                src={currentHeroLogo}
                alt={ep.name}
                className="max-h-full max-w-[280px] object-contain"
                onError={() => setHeroLogoIdx((i) => i + 1)}
                onLoad={(e) => { if (e.currentTarget.naturalWidth === 0) setHeroLogoIdx((i) => i + 1); }}
                data-testid={`ecosystem-modal-logo-${ep.slug}`}
              />
            ) : (
              <div className="luxe-display text-5xl" style={{ color: brand }}>{ep.name}</div>
            )}
          </div>

          <div className="luxe-eyebrow" style={{ color: brand }}>{ep.category}</div>
          <h3 className="luxe-display text-5xl md:text-6xl mt-3" style={{ color: "var(--luxe-ink)" }}>{ep.name}</h3>
          {ep.combo_headline_nl ? (
            <div className="luxe-display text-xl md:text-2xl mt-5 max-w-3xl mx-auto leading-tight" style={{ color: "var(--luxe-emerald)", fontWeight: 700 }}>
              {ep.combo_headline_nl}
            </div>
          ) : (
            <div className="text-xl md:text-2xl mt-4 max-w-2xl mx-auto" style={{ color: "var(--luxe-emerald)", fontFamily: "Lato, sans-serif", fontWeight: 400 }}>
              {ep.tagline_nl}
            </div>
          )}
        </div>

        {/* ───── DESCRIPTION ───── */}
        <div className="px-10 md:px-16 py-12">
          <p className="text-base leading-relaxed max-w-3xl" style={{ color: "var(--luxe-ink-soft)" }}>
            {ep.long_description_nl || ep.short_pitch_nl}
          </p>
        </div>

        {/* ───── STRONG FEATURES ───── */}
        <div className="px-10 md:px-16 pb-12" data-testid={`ecosystem-modal-features-${ep.slug}`}>
          <div className="luxe-hr-ornament text-[10px] uppercase tracking-[0.22em] font-semibold mb-8">
            <span>{t(p, "ecosystem_modal_features_label_nl", "Sterke punten")}</span>
          </div>
          <ul className="grid md:grid-cols-2 gap-x-10 gap-y-6">
            {(ep.strong_features_nl || []).map((f, i) => (
              <li key={i} className="flex items-start gap-5">
                <span className="luxe-display text-3xl flex-shrink-0 leading-none" style={{ color: brand }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm leading-relaxed pt-1.5" style={{ color: "var(--luxe-ink-soft)" }}>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* ───── BEX ENDORSEMENT QUOTE ───── */}
        {endorsement?.quote_nl && (
          <div
            className="px-10 md:px-16 py-14"
            style={{ background: "var(--luxe-ink)", color: "var(--luxe-cream)" }}
            data-testid={`ecosystem-modal-endorsement-${ep.slug}`}
          >
            <div className="luxe-hr-ornament text-[10px] uppercase tracking-[0.22em] font-semibold mb-8" style={{ color: "rgba(250,247,241,0.6)" }}>
              <span>Waarom Booking Experts kiest voor {ep.name}</span>
            </div>
            <div className="relative max-w-3xl">
              <div
                className="luxe-display absolute -top-6 -left-2 text-7xl leading-none select-none"
                style={{ color: "rgba(250,247,241,0.18)", fontStyle: "italic" }}
                aria-hidden
              >
                "
              </div>
              <blockquote
                className="luxe-italic text-2xl md:text-[28px] leading-snug relative z-10"
                style={{ color: "var(--luxe-cream)" }}
              >
                {endorsement.quote_nl}
              </blockquote>
            </div>
            <div className="mt-10 flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                style={{ background: "var(--luxe-emerald)", color: "var(--luxe-cream)", letterSpacing: "0.05em" }}
              >
                {authorInitials}
              </div>
              <div>
                <div className="text-sm font-semibold" style={{ color: "var(--luxe-cream)" }}>{endorsement.author_name}</div>
                <div className="text-xs mt-0.5" style={{ color: "rgba(250,247,241,0.65)" }}>{endorsement.author_role_nl}</div>
              </div>
            </div>
          </div>
        )}

        {/* ───── SERVICES & CTAs ───── */}
        <div className="px-10 md:px-16 py-12">
          <div className="luxe-hr-ornament text-[10px] uppercase tracking-[0.22em] font-semibold mb-6">
            <span>{t(p, "ecosystem_modal_services_label_nl", "Diensten & producten")}</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-10">
            {(ep.services_nl || []).map((s, i) => (
              <span
                key={i}
                className="text-xs px-4 py-2 border"
                style={{ borderColor: "var(--luxe-rule-strong)", borderRadius: 999, color: "var(--luxe-ink-soft)" }}
              >
                {s}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            {ep.website_url && (
              <a
                href={ep.website_url} target="_blank" rel="noopener noreferrer"
                className="luxe-btn-primary inline-flex items-center gap-3"
                data-testid={`ecosystem-modal-visit-${ep.slug}`}
              >
                {t(p, "ecosystem_modal_visit_label_nl", "Bezoek partner")} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            {p?.contact_email && (
              <a
                href={`mailto:${p.contact_email}?subject=${encodeURIComponent(`Demo aanvraag, ${ep.name} × ${p.name || "Booking Experts"} koppeling`)}`}
                className="luxe-btn-ghost inline-flex items-center gap-3"
                data-testid={`ecosystem-modal-demo-${ep.slug}`}
              >
                Plan een demo <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── FAQ ────────────────────── */
function Faq({ p }) {
  const faq = p.landing_faq || [];
  const [open, setOpen] = useState(0);
  if (!faq.length) return null;
  return (
    <section className="luxe-section" style={{ background: "var(--luxe-cream-2)" }} data-testid="landing-faq">
      <div className="container-x grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="luxe-eyebrow">{t(p, "faq_label_nl", "Veelgestelde vragen")}</div>
          <h2 className="luxe-display text-4xl md:text-5xl mt-6 leading-tight">
            {t(p, "faq_title_nl", `Alles wat ${p.name}-klanten ons vragen`)}
          </h2>
        </div>
        <div className="md:col-span-7">
          <hr className="luxe-rule" />
          {faq.map((item, i) => (
            <div key={i} className="border-b" style={{ borderColor: "var(--luxe-rule)" }}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="luxe-display text-xl md:text-2xl">{item.q}</span>
                <span className="flex-shrink-0 mt-2 w-8 h-8 rounded-full border flex items-center justify-center transition-colors" style={{ borderColor: "var(--luxe-rule-strong)" }}>
                  {open === i ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </span>
              </button>
              {open === i && (
                <div className="pb-6 text-sm leading-relaxed max-w-2xl" style={{ color: "var(--luxe-ink-soft)" }}>{item.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Contact block (editorial cinematic) ──────────────── */
function ContactBlock({ p, prefix, nav }) {
  return (
    <section className="luxe-section pt-0" data-testid="landing-contact">
      <div className="container-x">
        <div className="text-center max-w-3xl mx-auto">
          <div className="luxe-hr-ornament text-[10px] uppercase tracking-[0.22em] font-semibold mb-10">
            <span>Tot ziens op je park</span>
          </div>
          <h2 className="luxe-display text-5xl md:text-7xl leading-[1.05]">
            <span className="luxe-italic">
              {t(p, "final_cta_title_nl", "Klaar om jouw aankomstproces te stroomlijnen?")}
            </span>
          </h2>
          <p className="text-base md:text-lg mt-8 leading-relaxed" style={{ color: "var(--luxe-ink-soft)" }}>
            {t(p, "final_cta_body_nl", `Stel zelf samen of laat ons meedenken. Geen verkoopgesprek, gewoon iemand die ${p.name}-klanten kent.`)}
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-10">
            <button onClick={() => nav(`${prefix}/configurator`)} className="luxe-btn-primary inline-flex items-center gap-3" data-testid="landing-final-cta">
              {p.landing_cta_primary_nl || "Stel jouw bookingszuil samen"} <ArrowUpRight className="w-4 h-4" />
            </button>
            {p.contact_email && (
              <a
                href={`mailto:${p.contact_email}?subject=${encodeURIComponent("Kennismaking " + (p.name || ""))}`}
                className="luxe-btn-ghost inline-flex items-center gap-3"
                data-testid="landing-final-mail"
              >
                <MessageSquare className="w-4 h-4" /> {t(p, "final_cta_secondary_label_nl", "Mail ons")}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
